import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionContext = createContext(null);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);

  const fetchSubscription = useCallback(async () => {
    // Don't fetch until auth is resolved (user present from cookie OR token in memory)
    if (!token && !isAuthenticated) {
      setSubscription(null);
      setLoading(false);
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/api/subscriptions/current`, {
        headers,
        withCredentials: true,
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription({
        plan: 'trial',
        status: 'trialing',
        features: {
          max_estates: 1,
          max_reports: 1,
          collaboration: false,
          advanced_reports: false,
          bank_import: false,
          priority_support: false,
          api_access: false,
        },
        reports_used: 0,
        reports_limit: 1,
      });
    } finally {
      setLoading(false);
    }
  }, [token, isAuthenticated]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/subscriptions/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
    fetchPlans();
  }, [fetchSubscription, fetchPlans]);

  const createCheckout = async (plan, couponCode) => {
    if (!token) throw new Error('Must be logged in');

    const response = await axios.post(
      `${API_URL}/api/subscriptions/checkout`,
      {
        plan,
        origin_url: window.location.origin,
        coupon_code: couponCode || undefined,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    return response.data;
  };

  const recordReportUsed = async () => {
    if (!token) throw new Error('Must be logged in');

    const response = await axios.post(
      `${API_URL}/api/subscriptions/report-used`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // Update local state
    if (subscription) {
      setSubscription(prev => ({
        ...prev,
        reports_used: response.data.reports_used,
      }));
    }

    return response.data;
  };

  const checkReportLimit = async () => {
    if (!token) return { can_generate: true };

    try {
      const response = await axios.get(
        `${API_URL}/api/subscriptions/report-count`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Failed to check report limit:', error);
      return { can_generate: true, reports_used: 0, reports_limit: -1 };
    }
  };

  const cancelSubscription = async () => {
    if (!token) throw new Error('Must be logged in');

    await axios.post(
      `${API_URL}/api/subscriptions/cancel`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    await fetchSubscription();
  };

  const reactivateSubscription = async () => {
    if (!token) throw new Error('Must be logged in');

    await axios.post(
      `${API_URL}/api/subscriptions/reactivate`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    await fetchSubscription();
  };

  const getCustomerPortal = async () => {
    if (!token) throw new Error('Must be logged in');

    const response = await axios.get(
      `${API_URL}/api/subscriptions/portal`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    return response.data.portal_url;
  };

  const checkFeature = (feature) => {
    if (!subscription) return false;
    return subscription.features?.[feature] ?? false;
  };

  const canCreateEstate = (currentEstateCount) => {
    if (!subscription) return true;
    const maxEstates = subscription.features?.max_estates ?? 1;
    if (maxEstates === -1) return true;
    return currentEstateCount < maxEstates;
  };

  const isPro = subscription?.plan === 'professional' || subscription?.plan === 'per_estate';
  const isTrial = subscription?.plan === 'trial';
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';
  const isExpired = subscription?.status === 'expired';
  const canGenerateReport = subscription?.reports_limit === -1 || (subscription?.reports_used ?? 0) < (subscription?.reports_limit ?? 1);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        loading,
        plans,
        isPro,
        isTrial,
        isActive,
        isExpired,
        canGenerateReport,
        fetchSubscription,
        createCheckout,
        cancelSubscription,
        reactivateSubscription,
        getCustomerPortal,
        checkFeature,
        canCreateEstate,
        recordReportUsed,
        checkReportLimit,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
